/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Period 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/period/Period.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 21.
 */
import * as parent from '@components/form/field/Field';
import { Select } from '@components/form/field/select/Select';
import { Date as DateField } from '@components/form/field/date/Date';
import { Local } from '@models/store/list/Local';
import { Record } from '@models/store/Record';
import { Dom } from '@utils/Dom';
import { FormatDate } from '@utils/FormatDate';
import { Html } from '@utils/Html';
import { Language } from '@utils/Language';
import './Period.css';

type PeriodType = 'year' | 'month' | 'half' | 'quarter' | 'week' | 'date';

type PeriodValue = { start: FormatDate | string; end: FormatDate | string };
type PeriodResolvedValue = { period: PeriodType; start: FormatDate; end: FormatDate };

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 구간 타입
     */
    period: PeriodType;

    /**
     * 오늘 버튼 유무
     */
    today?: boolean;

    /**
     * 날짜 조절 버튼 유무
     */
    spinner?: boolean;

    /**
     * 필드 최소값
     */
    minValue?: string;

    /**
     * 필드 최댓값
     */
    maxValue?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Period extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'period';

    /**
     * 구간 타입
     */
    period: PeriodType;

    /**
     * 오늘 버튼 유무
     */
    today: boolean;

    /**
     * 날짜 조절 버튼 유무
     */
    spinner: boolean;

    /**
     * 필드 최소값
     */
    minValue: FormatDate;

    /**
     * 필드 최댓값
     */
    maxValue: FormatDate;

    /**
     * 년도 선택 Select 필드
     */
    year: Select = null;

    /**
     * 월 선택 Select 필드
     */
    month: Select = null;

    /**
     * 반기 선택 Select 필드
     */
    half: Select = null;

    /**
     * 분기 선택 Select 필드
     */
    quarter: Select = null;

    /**
     * 주차 선택 Select 필드
     */
    week: Select = null;

    /**
     * 날짜 선택 Date 필드
     */
    date: DateField = null;

    /**
     * 오늘 버튼 DOM
     */
    $today: Dom = null;

    /**
     * 이전 버튼 DOM
     */
    $prev: Dom = null;

    /**
     * 다음 버튼 DOM
     */
    $next: Dom = null;

    /**
     * Period 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Period> = null) {
        super(properties);

        this.today = this.properties?.today !== false;
        this.spinner = this.properties?.spinner !== false;
        this.minValue = new FormatDate(this.properties?.minValue ?? '2020-01-01').startOf('d');
        this.maxValue = new FormatDate(this.properties?.maxValue ?? new Date().getFullYear() + 1 + '-12-31').endOf('d');
    }

    /**
     * 년도 선택 Select 필드를 가져온다.
     *
     * @returns 년도 선택 Select 필드
     */
    getYear(): Select {
        if (this.year === null) {
            this.year = new Select({
                fieldWidth: 100,
                search: false,
                autoValue: true,
                initOnChange: false,
                store: new Local({
                    fields: [{ name: 'value', type: 'int' }],
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => {
                            const records = [];
                            let year = this.minValue.clone();

                            while (year.getYear() <= this.maxValue.getYear()) {
                                records.push({
                                    display: '',
                                    value: year.getYear(),
                                    start: year.clone().startOf('y'),
                                    end: year.clone().endOf('y'),
                                });
                                year.addYear(1);
                            }

                            return this.#filterAndClamp(records);
                        },
                    },
                }),
                listRenderer: async (_, record) => {
                    return record.get('start').format('E');
                },
                renderer: async (_, records) => {
                    if (records === null || Array.isArray(records) == true) {
                        return '';
                    }

                    const record = records;
                    return record.get('start').format('E');
                },
                listeners: {
                    change: async () => {
                        await this.#setValue('year');
                    },
                },
            });
        }

        return this.year;
    }

    /**
     * 월 선택 Select 필드를 가져온다.
     *
     * @returns 월 선택 Select 필드
     */
    getMonth(): Select {
        if (this.month === null) {
            this.month = new Select({
                fieldWidth: 80,
                search: false,
                autoValue: true,
                initOnChange: false,
                hidden: true,
                store: new Local({
                    fields: [{ name: 'value', type: 'int' }],
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => {
                            const yearValue = await this.getYear().getValue();
                            const year = yearValue ?? this.getRawValue().start.getYear();

                            const records = [];
                            let month = new FormatDate(`${year}-01-01`);
                            while (month.getYear() == year) {
                                records.push({
                                    display: '',
                                    value: month.getMonth(),
                                    start: month.clone().startOf('m'),
                                    end: month.clone().endOf('m'),
                                });
                                month.addMonth(1);
                            }

                            return this.#filterAndClamp(records);
                        },
                    },
                }),
                listRenderer: async (_, record) => {
                    return record.get('start').format('M');
                },
                renderer: async (_, records) => {
                    if (records === null || Array.isArray(records) == true) {
                        return '';
                    }

                    const record = records;
                    return record.get('start').format('M');
                },
                listeners: {
                    change: async () => {
                        await this.#setValue('month');
                    },
                },
            });
        }

        return this.month;
    }

    /**
     * 반기 선택 Select 필드를 가져온다.
     *
     * @returns 반기 선택 Select 필드
     */
    getHalf(): Select {
        if (this.half === null) {
            this.half = new Select({
                fieldWidth: 280,
                search: false,
                autoValue: true,
                initOnChange: false,
                hidden: true,
                store: new Local({
                    fields: [{ name: 'value', type: 'int' }],
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => {
                            const yearValue = await this.getYear().getValue();
                            const year = yearValue ?? this.getRawValue().start.getYear();

                            const halves = [
                                {
                                    display: '',
                                    value: 1,
                                    start: new FormatDate(`${year}-01-01`).startOf('d'),
                                    end: new FormatDate(`${year}-06-30`).endOf('d'),
                                },
                                {
                                    display: '',
                                    value: 2,
                                    start: new FormatDate(`${year}-07-01`).startOf('d'),
                                    end: new FormatDate(`${year}-12-31`).endOf('d'),
                                },
                            ];

                            return this.#filterAndClamp(halves);
                        },
                    },
                }),
                listRenderer: async (_, record) => {
                    return await Language.get('components.form.field.period.half', {
                        half: await Language.get(
                            record.get('value') === 1
                                ? 'components.form.field.period.first_half'
                                : 'components.form.field.period.second_half'
                        ).getText(),
                        period: this.formatRangeDisplay(record.get('start'), record.get('end')),
                    }).getText();
                },
                renderer: async (_, records) => {
                    if (records === null || Array.isArray(records) == true) {
                        return '';
                    }

                    const record = records;
                    return await Language.get('components.form.field.period.half', {
                        half: await Language.get(
                            record.get('value') === 1
                                ? 'components.form.field.period.first_half'
                                : 'components.form.field.period.second_half'
                        ).getText(),
                        period: this.formatRangeDisplay(record.get('start'), record.get('end')),
                    }).getText();
                },
                listeners: {
                    change: async () => {
                        await this.#setValue('half');
                    },
                },
            });
        }

        return this.half;
    }

    /**
     * 분기 선택 Select 필드를 가져온다.
     *
     * @returns 분기 선택 Select 필드
     */
    getQuarter(): Select {
        if (this.quarter === null) {
            this.quarter = new Select({
                fieldWidth: 280,
                search: false,
                autoValue: true,
                initOnChange: false,
                hidden: true,
                store: new Local({
                    fields: [{ name: 'value', type: 'int' }],
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => {
                            const yearValue = await this.getYear().getValue();
                            const year = yearValue ?? this.getRawValue().start.getYear();

                            const quarters = await Promise.all(
                                Array.from({ length: 4 }, async (_, i) => {
                                    const start = new FormatDate(`${year}-01-01`).startOf('y').add('m', i * 3);
                                    const end = start.clone().add('m', 2).endOf('m');

                                    return { display: '', value: i + 1, start, end };
                                })
                            );

                            return this.#filterAndClamp(quarters);
                        },
                    },
                }),
                listRenderer: async (_, record) => {
                    return await Language.get('components.form.field.period.quarter', {
                        quarter: record.get('value'),
                        period: this.formatRangeDisplay(record.get('start'), record.get('end')),
                    }).getText();
                },
                renderer: async (_, records) => {
                    if (records === null || Array.isArray(records) == true) {
                        return '';
                    }

                    const record = records;
                    return await Language.get('components.form.field.period.quarter', {
                        quarter: record.get('value'),
                        period: this.formatRangeDisplay(record.get('start'), record.get('end')),
                    }).getText();
                },
                listeners: {
                    change: async () => {
                        await this.#setValue('quarter');
                    },
                },
            });
        }

        return this.quarter;
    }

    /**
     * 주차 선택 Select 필드를 가져온다.
     *
     * @returns 주차 선택 Select 필드
     */
    getWeek(): Select {
        if (this.week === null) {
            this.week = new Select({
                fieldWidth: 280,
                search: false,
                autoValue: true,
                initOnChange: false,
                hidden: true,
                store: new Local({
                    fields: [{ name: 'value', type: 'int' }],
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => {
                            const yearValue = await this.getYear().getValue();
                            const year = yearValue ?? this.getRawValue().start.getYear();

                            const records = [];

                            // ISO 8601: 해당 연도 1주차 시작일 (1월 4일이 항상 1주차에 속함)
                            const cursor = new FormatDate(`${year}-01-04`).startOf('w');

                            while (true) {
                                const start = cursor.clone();
                                const end = cursor.clone().endOf('w');
                                const weekNum = start.format('W');

                                // ISO 8601: startOf('w')가 월요일이므로 +3 = 목요일
                                const thursday = start.clone().add('d', 3);
                                if (thursday.getYear() !== year) {
                                    break;
                                }

                                records.push({ display: '', value: Number(weekNum), start, end });
                                cursor.add('w', 1);
                            }

                            return this.#filterAndClamp(records);
                        },
                    },
                }),
                listRenderer: async (_, record) => {
                    return await Language.get('components.form.field.period.week', {
                        week: String(record.get('value')).padStart(2, '0'),
                        period: this.formatRangeDisplay(record.get('start'), record.get('end')),
                    }).getText();
                },
                renderer: async (_, records) => {
                    if (records === null || Array.isArray(records) == true) {
                        return '';
                    }

                    const record = records;
                    return await Language.get('components.form.field.period.week', {
                        week: String(record.get('value')).padStart(2, '0'),
                        period: this.formatRangeDisplay(record.get('start'), record.get('end')),
                    }).getText();
                },
                listeners: {
                    change: async () => {
                        await this.#setValue('week');
                    },
                },
            });
        }

        return this.week;
    }

    /**
     * 날짜 선택 Date 필드를 가져온다.
     *
     * @returns 날짜 선택 Date 필드
     */
    getDate(): DateField {
        if (this.date === null) {
            this.date = new DateField({
                fieldWidth: 160,
                format: 'Y-m-d',
                displayFormat: 'Y.m.d(D)',
                initOnChange: false,
                readonly: true,
                hidden: true,
                listeners: {
                    change: async () => {
                        await this.#setValue('date');
                    },
                },
            });
        }

        return this.date;
    }

    /**
     * 오늘 버튼 DOM 을 가져온다.
     *
     * @returns 오늘 버튼 DOM
     */
    $getToday(): Dom {
        if (this.$today === null) {
            this.$today = Html.create('button', {
                type: 'button',
                'data-action': 'today',
            });

            const $icon = Html.icon('mi mi-calendar');
            this.$today.append($icon);

            const $text = Html.create('span', {}, Language.get('buttons.today'));
            this.$today.append($text);

            this.$today.on('pointerdown', async () => {
                await this.setValue(this.#resolvePeriod(new FormatDate(), this.period ?? 'date'));
            });
        }

        return this.$today;
    }

    /**
     * 이전 버튼 DOM 을 가져온다.
     *
     * @returns 이전 버튼 DOM
     */
    $getPrev(): Dom {
        if (this.$prev === null) {
            this.$prev = Html.create('button', {
                type: 'button',
                'data-action': 'prev',
            });

            this.$prev.on('pointerdown', async (me) => {
                if (me.getAttr('disabled') !== '') return;
                const value = this.getRawValue();
                const date = value.start;

                const newValue = (() => {
                    switch (this.period) {
                        case 'year':
                            return date.clone().add('y', -1);
                        case 'month':
                            return date.clone().add('m', -1);
                        case 'half':
                            return date.clone().add('m', -6);
                        case 'quarter':
                            return date.clone().add('m', -3);
                        case 'week':
                            return date.clone().add('w', -1);
                        case 'date':
                            return date.clone().add('d', -1);
                    }
                })();
                await this.setValue(this.#resolvePeriod(newValue, this.period ?? 'date'));
            });
        }

        return this.$prev;
    }

    /**
     * 다음 버튼 DOM 을 가져온다.
     *
     * @returns 다음 버튼 DOM
     */
    $getNext(): Dom {
        if (this.$next === null) {
            this.$next = Html.create('button', {
                type: 'button',
                'data-action': 'next',
            });

            this.$next.on('pointerdown', async (me) => {
                if (me.getAttr('disabled') !== '') return;
                const value = this.getRawValue();
                const date = value.start;

                const newValue = (() => {
                    switch (this.period) {
                        case 'year':
                            return date.clone().add('y', 1);
                        case 'month':
                            return date.clone().add('m', 1);
                        case 'half':
                            return date.clone().add('m', 6);
                        case 'quarter':
                            return date.clone().add('m', 3);
                        case 'week':
                            return date.clone().add('w', 1);
                        case 'date':
                            return date.clone().add('d', 1);
                    }
                })();

                await this.setValue(this.#resolvePeriod(newValue, this.period ?? 'date'));
            });
        }

        return this.$next;
    }

    /**
     * 기간 범위 display 문자열을 반환한다.
     *
     * @param start - 시작일
     * @param end - 종료일
     * @returns display 문자열
     */
    formatRangeDisplay(start: FormatDate, end: FormatDate): string {
        return `${start.format('Y.m.d(D)')} ~ ${end.format('m.d(D)')}`;
    }

    /**
     * PeriodValue를 PeriodResolvedValue로 변환한다.
     * start/end 를 포함하는 가장 좁은 구간을 추론하고, 그 구간의 자연 경계를 clamp 하여 반환한다.
     *
     * @param value - 변환할 값
     */
    getValueToRaw(value: PeriodValue): PeriodResolvedValue {
        const start = typeof value.start === 'string' ? new FormatDate(value.start).startOf('d') : value.start;
        const end = typeof value.end === 'string' ? new FormatDate(value.end).startOf('d') : value.end;

        return this.#inferPeriod(start, end) ?? { period: this.properties.period ?? 'date', start, end };
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_update - 현재 period 상태에 따라 필드값을 변경해줄지 여부
     */
    async setValue(value: PeriodValue, is_init: boolean = false, is_update: boolean = true): Promise<void> {
        await this.lockValue();

        const rawValue = this.getValueToRaw(value);

        this.value = { start: rawValue.start, end: rawValue.end };
        this.#applyPeriodPanels(rawValue.period);

        if (is_update == true) {
            await this.#setFields(false);
        }

        await this.#updateSpinnerState();

        await this.releaseValue(rawValue, is_init);
    }

    /**
     * 구간 타입을 변경한다.
     * 현재 필드값(start)을 기준으로 변경할 구간의 자연 경계(start/end)를 재계산하여 반영한다.
     *
     * @param period - 변경할 구간
     * @param is_update - 각 필드의 값을 업데이트할지 여부
     */
    async setPeriod(period: PeriodType, is_update: boolean = true): Promise<void> {
        if (this.period == period) {
            return;
        }

        await this.lockValue();

        const rawValue = this.getRawValue();
        const { start, end } = this.#resolvePeriod(rawValue.start, period);

        this.value = { start, end };
        this.#applyPeriodPanels(period);

        if (is_update == true) {
            await this.#setFields(true);
        }

        await this.#updateSpinnerState();

        await this.releaseValue({ period, start, end }, false);
    }

    /**
     * 구간 타입에 따라 각 하위 필드의 노출여부를 갱신한다.
     *
     * @param period - 적용할 구간
     */
    #applyPeriodPanels(period: PeriodType): void {
        this.period = period;

        const subPanels: Exclude<PeriodType, 'year'>[] = ['month', 'half', 'quarter', 'week', 'date'];
        const subPanelGetters: { [K in Exclude<PeriodType, 'year'>]: () => { hide(): void; show(): void } } = {
            month: () => this.getMonth(),
            half: () => this.getHalf(),
            quarter: () => this.getQuarter(),
            week: () => this.getWeek(),
            date: () => this.getDate(),
        };

        if (this.period === 'date') {
            this.getYear().hide();
            subPanels.filter((panel) => panel !== 'date').forEach((panel) => subPanelGetters[panel]().hide());
            this.getDate().show();
            this.getDate().setReadonly(false);
        } else {
            this.getYear().show();
            subPanels.filter((panel) => panel !== this.period).forEach((panel) => subPanelGetters[panel]().hide());
            if (this.period !== 'year') {
                subPanelGetters[this.period]().show();
            }

            this.getDate().setReadonly(true);
        }
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<{ start: string; end: string }> {
        const value = this.getRawValue();

        return { start: value.start.format('Y-m-d'), end: value.end.format('Y-m-d') };
    }

    /**
     * 현재 필드값을 FormatDate 형식으로 가져온다.
     *
     * @returns FormatDate 필드값
     */
    getRawValue(): PeriodResolvedValue {
        const value = this.value ?? {
            start: new FormatDate().startOf('d'),
            end: new FormatDate().endOf('d'),
        };

        value.period = this.period;

        return value;
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        if (this.spinner === true) {
            this.$getContent().append(this.$getPrev());
        }

        this.$getContent().append(this.getYear().$getComponent());
        await this.getYear().render();

        this.$getContent().append(this.getMonth().$getComponent());
        await this.getMonth().render();

        this.$getContent().append(this.getHalf().$getComponent());
        await this.getHalf().render();

        this.$getContent().append(this.getQuarter().$getComponent());
        await this.getQuarter().render();

        this.$getContent().append(this.getWeek().$getComponent());
        await this.getWeek().render();

        this.$getContent().append(this.getDate().$getComponent());
        await this.getDate().render();

        if (this.today === true) {
            this.$getContent().append(this.$getToday());
        }

        if (this.spinner === true) {
            this.$getContent().append(this.$getNext());
        }
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.initValue === null) {
            await this.setValue(this.#resolvePeriod(new FormatDate(), this.properties.period ?? 'date'));
        }

        await super.renderState();
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.year?.remove(false);
        this.month?.remove(false);
        this.half?.remove(false);
        this.quarter?.remove(false);
        this.week?.remove(false);
        this.date?.remove(false);

        await super.remove(removeDom);
    }

    /**
     * start/end를 minValue~maxValue 범위로 클램핑한다.
     *
     * @param start - 시작일
     * @param end - 종료일
     */
    #clamp(start: FormatDate, end: FormatDate): { start: FormatDate; end: FormatDate } {
        return {
            start: start.getTime() < this.minValue.getTime() ? this.minValue.clone() : start,
            end: end.getTime() > this.maxValue.getTime() ? this.maxValue.clone() : end,
        };
    }

    /**
     * 레코드 배열에서 minValue~maxValue 범위와 겹치는 항목만 남기고, start/end를 범위 내로 클램핑한다.
     *
     * @param records - 레코드 배열
     */
    #filterAndClamp<T extends { start: FormatDate; end: FormatDate }>(records: T[]): T[] {
        return records
            .filter((r) => r.end.getTime() >= this.minValue.getTime() && r.start.getTime() <= this.maxValue.getTime())
            .map((r) => ({ ...r, ...this.#clamp(r.start, r.end) }));
    }

    /**
     * start/end 를 포함하는 가장 좁은 구간을 추론하고, 그 구간의 자연 경계를 clamp 하여 반환한다.
     * 추론 우선순위: date → week → month → quarter → half → year
     * 어느 구간에도 속하지 않으면 (start/end 가 서로 다른 연도에 걸치는 등) null을 반환한다.
     *
     * @param start - 시작일
     * @param end - 종료일
     */
    #inferPeriod(start: FormatDate, end: FormatDate): PeriodResolvedValue | null {
        const s = start.clone().startOf('d');
        const e = end.clone().startOf('d');

        // date: 같은 날
        if (
            s.getYear() === e.getYear() &&
            s.getMonth() === e.getMonth() &&
            Number(s.format('j')) === Number(e.format('j'))
        ) {
            return { period: 'date', ...this.#clamp(s, s.clone().endOf('d')) };
        }

        // week: 같은 ISO 주차 (월요일 ~ 일요일)
        const startWeekMon = s.clone().startOf('w');
        if (startWeekMon.getTime() === e.clone().startOf('w').getTime()) {
            return { period: 'week', ...this.#clamp(startWeekMon, startWeekMon.clone().endOf('w')) };
        }

        // month: 같은 달
        if (s.getYear() === e.getYear() && s.getMonth() === e.getMonth()) {
            return { period: 'month', ...this.#clamp(s.clone().startOf('m'), s.clone().endOf('m')) };
        }

        // quarter: 같은 분기
        const sQuarter = Math.ceil(s.getMonth() / 3);
        if (s.getYear() === e.getYear() && sQuarter === Math.ceil(e.getMonth() / 3)) {
            const qStart = new FormatDate(
                `${s.getYear()}-${String((sQuarter - 1) * 3 + 1).padStart(2, '0')}-01`
            ).startOf('d');
            return { period: 'quarter', ...this.#clamp(qStart, qStart.clone().add('m', 2).endOf('m')) };
        }

        // half: 같은 반기
        const sHalf = s.getMonth() <= 6 ? 1 : 2;
        if (s.getYear() === e.getYear() && sHalf === (e.getMonth() <= 6 ? 1 : 2)) {
            const hStart =
                sHalf === 1
                    ? new FormatDate(`${s.getYear()}-01-01`).startOf('d')
                    : new FormatDate(`${s.getYear()}-07-01`).startOf('d');
            const hEnd =
                sHalf === 1
                    ? new FormatDate(`${s.getYear()}-06-30`).endOf('d')
                    : new FormatDate(`${s.getYear()}-12-31`).endOf('d');
            return { period: 'half', ...this.#clamp(hStart, hEnd) };
        }

        // year: 같은 연도
        if (s.getYear() === e.getYear()) {
            return {
                period: 'year',
                ...this.#clamp(
                    new FormatDate(`${s.getYear()}-01-01`).startOf('d'),
                    new FormatDate(`${s.getYear()}-12-31`).endOf('d')
                ),
            };
        }

        return null;
    }

    /**
     * 특정 날짜와 구간 타입을 받아, 그 날짜가 속하는 구간의 시작일/종료일을 반환한다.
     * 날짜가 minValue~maxValue 범위 밖이면 가장 가까운 경계로 보정 후 구간을 구하고, clamp 하여 반환한다.
     *
     * @param date - 기준 날짜
     * @param period - 구간 타입
     */
    #resolvePeriod(date: FormatDate, period: PeriodType): { start: FormatDate; end: FormatDate } {
        const d = (
            date.getTime() < this.minValue.getTime()
                ? this.minValue.clone()
                : date.getTime() > this.maxValue.getTime()
                  ? this.maxValue.clone()
                  : date.clone()
        ).startOf('d');

        switch (period) {
            case 'date':
                return this.#clamp(d, d.clone().endOf('d'));

            case 'week': {
                const weekStart = d.clone().startOf('w');
                return this.#clamp(weekStart, weekStart.clone().endOf('w'));
            }

            case 'month':
                return this.#clamp(d.clone().startOf('m'), d.clone().endOf('m'));

            case 'quarter': {
                const q = Math.ceil(d.getMonth() / 3);
                const qStart = new FormatDate(`${d.getYear()}-${String((q - 1) * 3 + 1).padStart(2, '0')}-01`).startOf(
                    'd'
                );
                return this.#clamp(qStart, qStart.clone().add('m', 2).endOf('m'));
            }

            case 'half': {
                const half = d.getMonth() <= 6 ? 1 : 2;
                const hStart =
                    half === 1
                        ? new FormatDate(`${d.getYear()}-01-01`).startOf('d')
                        : new FormatDate(`${d.getYear()}-07-01`).startOf('d');
                const hEnd =
                    half === 1
                        ? new FormatDate(`${d.getYear()}-06-30`).endOf('d')
                        : new FormatDate(`${d.getYear()}-12-31`).endOf('d');
                return this.#clamp(hStart, hEnd);
            }

            case 'year':
                return this.#clamp(
                    new FormatDate(`${d.getYear()}-01-01`).startOf('d'),
                    new FormatDate(`${d.getYear()}-12-31`).endOf('d')
                );
        }
    }

    /**
     * 필드값이 변경되었을 경우, 각각의 구간별 필드값을 변경한다.
     *
     * @param is_update - 구간별 필드의 값을 갱신하고, 그 결과에 따라 구간필드 값을 업데이트할 지 여부
     */
    async #setFields(is_update: boolean = false): Promise<void> {
        const rawValue = this.getRawValue();

        let isReload: boolean = false;
        if (this.period !== 'date') {
            if (rawValue.start.getYear() != (await this.getYear().getValue())) {
                await this.getYear().setValue(rawValue.start.getYear(), is_update == false);
                isReload = true;
            }
        }

        if (this.period === 'month') {
            if (isReload == true) {
                await this.getMonth().getStore().reload();
            }
            await this.getMonth().setValue(rawValue.start.getMonth(), is_update == false);
        }

        if (this.period === 'half') {
            if (isReload == true) {
                await this.getHalf().getStore().reload();
            }
            await this.getHalf().setValue(Number(rawValue.start.format('R')), is_update == false);
        }

        if (this.period === 'quarter') {
            if (isReload == true) {
                await this.getQuarter().getStore().reload();
            }
            await this.getQuarter().setValue(Number(rawValue.start.format('Q')), is_update == false);
        }

        if (this.period === 'week') {
            if (isReload == true) {
                await this.getWeek().getStore().reload();
            }
            await this.getWeek().setValue(Number(rawValue.start.format('W')), is_update == false);
        }

        if (this.period === 'date') {
            await this.getDate().setValue(rawValue.start.format('Y-m-d'), is_update == false);
        }
    }

    /**
     * 각각의 필드에서 값이 변경되었을 때, 필드값을 지정한다.
     *
     * @param field - 변경된 필드명
     */
    async #setValue(field: PeriodType): Promise<void> {
        let record: Record = null;
        if (this.period == 'year') {
            if (field == 'year') {
                record = await this.getYear().getValueToRecordOne();
            }
        }

        if (this.period == 'month') {
            if (field == 'year' || field == 'month') {
                if (field == 'year') {
                    await this.getMonth().getStore().reload();
                }

                record = await this.getMonth().getValueToRecordOne();
            }
        }

        if (this.period == 'half') {
            if (field == 'year' || field == 'half') {
                if (field == 'year') {
                    await this.getHalf().getStore().reload();
                }

                record = await this.getHalf().getValueToRecordOne();
            }
        }

        if (this.period == 'quarter') {
            if (field == 'year' || field == 'quarter') {
                if (field == 'year') {
                    await this.getQuarter().getStore().reload();
                }

                record = await this.getQuarter().getValueToRecordOne();
            }
        }

        if (this.period == 'week') {
            if (field == 'year' || field == 'week') {
                if (field == 'year') {
                    await this.getWeek().getStore().reload();
                }

                record = await this.getWeek().getValueToRecordOne();
            }
        }

        if (this.period === 'date') {
            if (field == 'date') {
                const date = await this.getDate().getValue();

                if (this.isLoading() == false) {
                    await this.setValue({ start: new FormatDate(date), end: new FormatDate(date) }, false, false);
                }
            }
        } else if (record !== null) {
            if (this.isLoading() == false) {
                await this.setValue({ start: record.get('start'), end: record.get('end') }, false, false);
            }
        }
    }

    /**
     * 이전/다음 버튼 비활성화 상태를 업데이트한다.
     */
    async #updateSpinnerState(): Promise<void> {
        if (this.spinner !== true) return;

        const value = this.getRawValue();

        if (value.start.getTime() <= this.minValue.getTime()) {
            this.$getPrev().disable();
        } else {
            this.$getPrev().enable();
        }

        if (value.end.getTime() >= this.maxValue.getTime()) {
            this.$getNext().disable();
        } else {
            this.$getNext().enable();
        }
    }
}
